import { WIDGET_LOGICAL_NAMES, METRIC_NAMES, VALIDATION_TYPES } from '@core/config/Consts';
import AlertNumeric from '@components/common/alerts/alert-numeric';
import AlertDuration from '@components/common/alerts/alert-duration';
import AlertTimeframe from '@components/common/alerts/alert-timeframe';
import AlertStateDuration from '@components/common/alerts/alert-state-duration';
import AlertNumericWithAdd from '@components/common/alerts/alert-numeric-with-add';

const globalValidationProps = {
  type: VALIDATION_TYPES.NUMERIC,
  from: 0,
  to: 100000,
};

const percentageIntValidationProps = {
  type: VALIDATION_TYPES.NUMERIC,
  from: 0,
  to: 999, // Gal confirmed load percentage can be greater than 100
};

const percentageTimeframeValidationProps = {
  type: VALIDATION_TYPES.PERCENTAGE,
  from: 0,
  to: 100,
};

const durationValidation = {
  type: VALIDATION_TYPES.DURATION,
};

const CSATValidation = {
  below: {
    from: 1, to: 100, maxChars: 3, message: 'Please enter values between 1 and 100',
  },
  above: {
    from: 0, to: 99, maxChars: 2, message: 'Please enter values between 0 and 99',
  },
  general: { message: 'Enter valid values' },
};

const numericWithAddValidation = {
  below: {
    from: 1, to: 999, maxChars: 3, message: 'Please enter values between 1 and 999',
  },
  above: {
    from: 0, to: 999, maxChars: 3, message: 'Please enter values between 0 and 999',
  },
  general: { message: 'Enter valid values' },
};

const NUMERIC_DIALOG_WIDTH = '475px';
const DURATION_DIALOG_WIDTH = '620px';
const STATE_DURATION_DIALOG_WIDTH = '635px';
const TIMEFRAME_DIALOG_WIDTH = '642px';

const timeframeMetricSize = {
  width: TIMEFRAME_DIALOG_WIDTH,
  height: 'initial',
};

const singleMetricSize = {
  width: NUMERIC_DIALOG_WIDTH,
  height: '220px',
};

const twoMetricSize = {
  width: NUMERIC_DIALOG_WIDTH,
  height: '300px',
};

const threeMetricSize = {
  width: NUMERIC_DIALOG_WIDTH,
  height: '400px',
};

const fourMetricSize = {
  width: NUMERIC_DIALOG_WIDTH,
  height: '470px',
};

const twoMetricSizeDuration = {
  width: DURATION_DIALOG_WIDTH,
  height: '300px',
};

const singleMetricSizeDuration = {
  width: STATE_DURATION_DIALOG_WIDTH,
  height: 'unset',
};

const csatMetricSize = {
  width: NUMERIC_DIALOG_WIDTH,
  height: 'unset',
};

const ALERT_CONFIG = {
  [WIDGET_LOGICAL_NAMES.SHIFT_STATUS]: {
    [METRIC_NAMES.CSAT]: {
      nameI18n: 'metric_csat',
      size: singleMetricSize,
      showAlertConfiguration: true,
      component: AlertNumeric,
      validation: percentageIntValidationProps,
    },
    [METRIC_NAMES.CLOSED_CONVERSATIONS]: {
      nameI18n: 'metric_closed_conversation',
      size: fourMetricSize,
      showAlertConfiguration: true,
      component: AlertNumeric,
      validation: globalValidationProps,
    },
    [METRIC_NAMES.CLOSED_BY_CONSUMER]: {
      nameI18n: 'metric_closed_by_consumer',
      size: fourMetricSize,
      showAlertConfiguration: false,
      component: AlertNumeric,
      validation: globalValidationProps,
    },
    [METRIC_NAMES.CLOSED_BY_AGENT]: {
      nameI18n: 'metric_closed_by_agent',
      size: fourMetricSize,
      showAlertConfiguration: false,
      component: AlertNumeric,
      validation: globalValidationProps,
    },
    [METRIC_NAMES.AUTO_CLOSED]: {
      nameI18n: 'metric_auto_closed',
      size: fourMetricSize,
      showAlertConfiguration: false,
      component: AlertNumeric,
      validation: globalValidationProps,
    },
    [METRIC_NAMES.LOAD_CONVERSATIONS]: {
      nameI18n: 'metric_load',
      size: threeMetricSize,
      showAlertConfiguration: true,
      component: AlertNumeric,
      validation: percentageIntValidationProps,
    },
    [METRIC_NAMES.HUMAN_ONLINE_LOAD]: {
      nameI18n: 'metric_humanOnlineLoad',
      size: threeMetricSize,
      showAlertConfiguration: false,
      component: AlertNumeric,
      validation: percentageIntValidationProps,
    },
    [METRIC_NAMES.HUMAN_AWAY_LOAD]: {
      nameI18n: 'metric_humanAwayLoad',
      size: threeMetricSize,
      showAlertConfiguration: false,
      component: AlertNumeric,
      validation: percentageIntValidationProps,
    },
    [METRIC_NAMES.ASSIGNED_CONVERSATIONS]: {
      nameI18n: 'metric_assigned_conversation',
      size: twoMetricSize,
      showAlertConfiguration: true,
      component: AlertNumeric,
      validation: globalValidationProps,
    },
    [METRIC_NAMES.OVERDUE_ASSIGNED]: {
      nameI18n: 'metric_overdue_assigned',
      size: twoMetricSize,
      showAlertConfiguration: false,
      component: AlertNumeric,
      validation: globalValidationProps,
    },
    [METRIC_NAMES.AVG_WAIT_TIME_FIRST_RESPONSE]: {
      nameI18n: 'metric_avg_wait_time_first_response',
      size: twoMetricSizeDuration,
      showAlertConfiguration: true,
      component: AlertDuration,
      validation: durationValidation,
    },
    [METRIC_NAMES.AVG_TIME_TO_FIRST_RESPONSE_FIRST_ASSIGNMENT]: {
      nameI18n: 'metric_avg_time_to_first_response_first_assignment',
      size: twoMetricSizeDuration,
      showAlertConfiguration: false,
      component: AlertDuration,
      validation: durationValidation,
    },
    [METRIC_NAMES.AVG_WAIT_TIME]: {
      nameI18n: 'metric_avg_wait_time',
      size: twoMetricSizeDuration,
      showAlertConfiguration: true,
      component: AlertDuration,
      validation: durationValidation,
    },
    [METRIC_NAMES.AVG_TIME_TO_RESPONSE]: {
      nameI18n: 'metric_avg_time_to_response',
      size: twoMetricSizeDuration,
      showAlertConfiguration: false,
      component: AlertDuration,
      validation: durationValidation,
    },
    [METRIC_NAMES.AVG_CONVERSATIONS_DURATION]: {
      nameI18n: 'metric_avg_conversations_duration',
      size: singleMetricSize,
      showAlertConfiguration: true,
      component: AlertDuration,
      validation: durationValidation,
    },
  },
  [WIDGET_LOGICAL_NAMES.IN_QUEUE]: {
    [METRIC_NAMES.IN_QUEUE_UNASSIGNED_TOTAL]: {
      nameI18n: 'metric_total_unassigned_conversations',
      size: twoMetricSize,
      showAlertConfiguration: true,
      component: AlertNumeric,
      validation: globalValidationProps,
    },
    [METRIC_NAMES.OVERDUE_IN_QUEUE]: {
      nameI18n: 'metric_overdue_in_queue',
      size: twoMetricSize,
      showAlertConfiguration: false,
      component: AlertNumeric,
      validation: globalValidationProps,
    },
    [METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS]: {
      nameI18n: 'alerts_configuration_queue_summary_in_queue',
      size: twoMetricSizeDuration,
      showAlertConfiguration: false,
      component: AlertNumeric,
      validation: globalValidationProps,
    },
    [METRIC_NAMES.IN_QUEUE_SKILL_WAIT_TIME]: {
      nameI18n: 'alerts_configuration_queue_summary_wait_time',
      size: twoMetricSizeDuration,
      showAlertConfiguration: false,
      component: AlertDuration,
      validation: durationValidation,
    },
  },
  [WIDGET_LOGICAL_NAMES.AGENTS]: {
    [METRIC_NAMES.AGENT_VIEW_AGENT_STATUS]: {
      nameI18n: 'alerts_configuration_metric_online_agent_status',
      size: timeframeMetricSize,
      showAlertConfiguration: true,
      component: AlertTimeframe,
      validation: percentageTimeframeValidationProps,
    },
    [METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION]: {
      nameI18n: 'metric_agent_status_duration',
      size: singleMetricSizeDuration,
      showAlertConfiguration: true,
      component: AlertStateDuration,
      validation: globalValidationProps,
    },
    [METRIC_NAMES.AGENT_VIEW_CSAT]: {
      nameI18n: 'metric_csat',
      size: csatMetricSize,
      showAlertConfiguration: true,
      component: AlertNumericWithAdd,
      validation: CSATValidation,
    },
    [METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE]: {
      nameI18n: 'metric_transfer_rate_agents_widget',
      size: singleMetricSizeDuration,
      showAlertConfiguration: true,
      component: AlertNumericWithAdd,
      validation: numericWithAddValidation,
    },
    [METRIC_NAMES.AGENT_VIEW_AGENT_LOAD]: {
      nameI18n: 'metric_load',
      size: singleMetricSizeDuration,
      showAlertConfiguration: true,
      component: AlertNumericWithAdd,
      validation: numericWithAddValidation,
    },
  },
  [WIDGET_LOGICAL_NAMES.BOTS]: {
    [METRIC_NAMES.AGENT_VIEW_PENDING_RATE]: {
      nameI18n: 'metric_pending',
      size: singleMetricSizeDuration,
      showAlertConfiguration: true,
      component: AlertNumericWithAdd,
      validation: numericWithAddValidation,
    },
  },
};

export {
  ALERT_CONFIG,
};
